import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Tabs, 
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { employeesAPI, projectsAPI, invoicesAPI, clientsAPI } from '../services/api';
import { formatNumber } from '../utils/formatNumber';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const Reports: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Fetch data
  const { data: employees = [], isLoading: employeesLoading } = useQuery({
    queryKey: ['employees'],
    queryFn: employeesAPI.getAll
  });

  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsAPI.getAll
  });

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices', selectedYear],
    queryFn: invoicesAPI.getAll
  });

  const { data: clients = [], isLoading: clientsLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: clientsAPI.getAll
  });

  // Calculate analytics
  const activeProjects = projects.filter((proj: any) => proj.isActive && new Date(proj.startDate).getFullYear() <= selectedYear);
  const completedProjects = projects.filter((proj: any) => !proj.isActive && new Date(proj.startDate).getFullYear() <= selectedYear);
  
  // Filter employees by hire year
  const employeesInYear = employees.filter((emp: any) => new Date(emp.hireDate).getFullYear() <= selectedYear);

  // Employee distribution by project
  const employeeProjectData = projects.filter((proj: any) => new Date(proj.startDate).getFullYear() <= selectedYear).map((project: any) => ({
    name: project.name,
    employees: project.employees?.length || 0,
    shadowEmployees: project.employees?.filter((emp: any) => emp.shadow).length || 0,
    regularEmployees: project.employees?.filter((emp: any) => !emp.shadow).length || 0
  }));

  // Invoice analytics by month
  const invoiceMonthlyData = Array.from({ length: 12 }, (_, i) => {
    const monthInvoices = invoices.filter((inv: any) => {
      const invoiceDate = new Date(inv.dueDate);
      return invoiceDate.getMonth() === i && invoiceDate.getFullYear() === selectedYear;
    });
    
    return {
      month: new Date(2024, i, 1).toLocaleDateString('en-US', { month: 'short' }),
      total: monthInvoices.reduce((sum: number, inv: any) => sum + (inv.totalAmount || 0), 0),
      count: monthInvoices.length,
      paid: monthInvoices.filter((inv: any) => inv.status === 'PAID').length,
      unpaid: monthInvoices.filter((inv: any) => inv.status === 'UNPAID').length
    };
  });

  // Year-over-year comparison
  const currentYearInvoices = invoices.filter((inv: any) => 
    new Date(inv.dueDate).getFullYear() === selectedYear
  );
  const previousYearInvoices = invoices.filter((inv: any) => 
    new Date(inv.dueDate).getFullYear() === selectedYear - 1
  );

  const yearComparison = [
    {
      year: selectedYear - 1,
      total: previousYearInvoices.reduce((sum: number, inv: any) => sum + (inv.totalAmount || 0), 0),
      count: previousYearInvoices.length
    },
    {
      year: selectedYear,
      total: currentYearInvoices.reduce((sum: number, inv: any) => sum + (inv.totalAmount || 0), 0),
      count: currentYearInvoices.length
    }
  ];

  // Client revenue
  const clientRevenue = clients.map((client: any) => {
    const clientInvoices = invoices.filter((inv: any) => 
      inv.client?.name === client.name && 
      new Date(inv.dueDate).getFullYear() === selectedYear
    );
    return {
      name: client.name,
      revenue: clientInvoices.reduce((sum: number, inv: any) => sum + (inv.totalAmount || 0), 0),
      invoiceCount: clientInvoices.length
    };
  }).sort((a: any, b: any) => b.revenue - a.revenue);

  // Employment type distribution
  const employmentTypeData = [
    { name: 'Contract', value: employeesInYear.filter((emp: any) => emp.employmentType === 'CONTRACT').length },
    { name: 'Freelance', value: employeesInYear.filter((emp: any) => emp.employmentType === 'FREELANCE').length }
  ];
  
  // Employee hiring trends by year
  const hiringTrends = Array.from({ length: 5 }, (_, i) => {
    const year = selectedYear - 4 + i;
    const hiredInYear = employees.filter((emp: any) => new Date(emp.hireDate).getFullYear() === year);
    return {
      year,
      hired: hiredInYear.length,
      active: employees.filter((emp: any) => new Date(emp.hireDate).getFullYear() <= year && emp.isActive).length
    };
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const isLoading = employeesLoading || projectsLoading || invoicesLoading || clientsLoading;

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Loading reports...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100vh', overflow: 'auto' }}>
      <Typography variant="h5"fontWeight={700}>
        Analytics & Reports
      </Typography>

      {/* Year Selector */}
      <Box sx={{ my: 3 }}>
        <FormControl sx={{ minWidth: 120, width: '100%' }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={selectedYear}
            label="Year"
            onChange={(e) => setSelectedYear(e.target.value as number)}
          >
            {[2022, 2023, 2024, 2025, 2026].map((year) => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Employee Analytics" />
          <Tab label="Project Analytics" />
          <Tab label="Financial Reports" />
          <Tab label="Client Analytics" />
        </Tabs>
      </Box>

      {/* Employee Analytics Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Paper sx={{ p: 3, height: 500, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>Employee Hiring Trends (5 Years)</Typography>
            <Box sx={{ flex: 1, minHeight: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hiringTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="hired" fill="#8884d8" name="Hired This Year" />
                  <Bar dataKey="active" fill="#82ca9d" name="Total Active" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, height: 500, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>Employment Type Distribution</Typography>
            <Box sx={{ flex: 1, minHeight: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={employmentTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {employmentTypeData.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, height: 500, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>Employees per Project ({selectedYear})</Typography>
            <Box sx={{ flex: 1, minHeight: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={employeeProjectData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="regularEmployees" fill="#8884d8" name="Regular" />
                  <Bar dataKey="shadowEmployees" fill="#82ca9d" name="Shadow" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
      </TabPanel>

      {/* Project Analytics Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Paper sx={{ p: 3, height: 500, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>Project Status Overview ({selectedYear})</Typography>
            <Box sx={{ flex: 1, minHeight: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { name: 'Active Projects', value: activeProjects.length, fill: '#4caf50' },
                  { name: 'Completed Projects', value: completedProjects.length, fill: '#ff9800' }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, height: 500, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>Employee Distribution by Project ({selectedYear})</Typography>
            <Box sx={{ flex: 1, minHeight: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={employeeProjectData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="employees" fill="#1976d2" name="Total Employees" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
      </TabPanel>

      {/* Financial Reports Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Paper sx={{ p: 3, height: 500, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>Monthly Revenue ({selectedYear})</Typography>
              <Box sx={{ flex: 1, minHeight: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={invoiceMonthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`€${formatNumber(value.toLocaleString())}`, 'Revenue']} />
                    <Area type="monotone" dataKey="total" stroke="#1976d2" fill="#1976d2" fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

            <Paper sx={{ p: 3, height: 500, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>Year-over-Year Comparison</Typography>
              <Box sx={{ flex: 1, minHeight: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={yearComparison}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`€${formatNumber(value.toLocaleString())}`, 'Revenue']} />
                    <Legend />
                    <Bar dataKey="total" fill="#1976d2" name="Revenue" />
                    <Bar dataKey="count" fill="#82ca9d" name="Invoice Count" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Box>

          <Paper sx={{ p: 3, height: 500, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>Monthly Invoice Status</Typography>
            <Box sx={{ flex: 1, minHeight: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={invoiceMonthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="paid" stroke="#4caf50" name="Paid" />
                  <Line type="monotone" dataKey="unpaid" stroke="#f44336" name="Unpaid" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
      </TabPanel>

      {/* Client Analytics Tab */}
      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Paper sx={{ p: 3, height: 500, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>Top Clients by Revenue</Typography>
            <Box sx={{ flex: 1, minHeight: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={clientRevenue.slice(0, 8)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip formatter={(value) => [`€${formatNumber(value.toLocaleString())}`, 'Revenue']} />
                  <Bar dataKey="revenue" fill="#ff9800" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, height: 500, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>Client Invoice Count</Typography>
            <Box sx={{ flex: 1, minHeight: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={clientRevenue.slice(0, 8)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="invoiceCount" fill="#9c27b0" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
      </TabPanel>
    </Box>
  );
};

export default Reports 